import { useState, useEffect, useCallback, useMemo } from "react";
import { Router, RefreshCw, Power, Upload, X, CircleDot, Shield, Save, Info, ClipboardCopy, RotateCcw, Send, User, Cpu, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const P_MODEL = "InternetGatewayDevice.DeviceInfo.ModelName";
const P_FW = "InternetGatewayDevice.DeviceInfo.SoftwareVersion";
const P_IP = "InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ExternalIPAddress";

const isOnline = (d) => !!d?.last_inform && Date.now() - new Date(d.last_inform).getTime() < 15 * 60 * 1000;
const modelOf = (d) => (d.params || {})[P_MODEL] || d.model || d.product_class || "CPE";
const fwOf = (d) => (d.params || {})[P_FW] || "—";

export default function RoutersPanel({ role, onClose }) {
  const { t } = useI18n();
  const isAdmin = role === "admin";
  const [devs, setDevs] = useState([]);
  const [clients, setClients] = useState({});
  const [filter, setFilter] = useState("all"); // all | online | offline
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [fwUrl, setFwUrl] = useState("");
  const [cfg, setCfg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dFw, setDFw] = useState("");
  const [dCfg, setDCfg] = useState({ wifi_ssid: "", wifi_pass: "", pppoe_user: "", pppoe_pass: "" });
  const [pGet, setPGet] = useState("");
  const [pSet, setPSet] = useState({ name: "", value: "" });
  const [hist, setHist] = useState([]);
  const acsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/tr069/acs`;
  const copyUrl = () => { navigator.clipboard?.writeText(acsUrl); toast.success(t("Skopiowano adres ACS")); };

  const load = useCallback(() => {
    api.get("/tr069/devices").then((r) => setDevs(r.data || [])).catch(() => setDevs([]));
    api.get("/clients").then((r) => {
      const m = {}; (r.data || []).forEach((c) => { m[c.id] = c.name || c.address || c.id; }); setClients(m);
    }).catch(() => {});
    api.get("/tr069/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => api.get("/tr069/devices").then((r) => setDevs(r.data || [])).catch(() => {}), 15000);
    return () => clearInterval(id);
  }, []);

  const rows = useMemo(() => {
    let r = devs;
    if (filter === "online") r = r.filter(isOnline);
    if (filter === "offline") r = r.filter((d) => !isOnline(d));
    const s = q.trim().toLowerCase();
    if (s) r = r.filter((d) => `${modelOf(d)} ${d.serial} ${fwOf(d)} ${clients[d.client_id] || ""}`.toLowerCase().includes(s));
    return r;
  }, [devs, filter, q, clients]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const toggle = (id) => setSel((v) => ({ ...v, [id]: !v[id] }));
  const allChecked = rows.length > 0 && rows.every((d) => sel[d.id]);
  const toggleAll = () => { const nv = {}; if (!allChecked) rows.forEach((d) => { nv[d.id] = true; }); setSel(nv); };

  const bulk = async (type, params) => {
    if (!selIds.length) { toast.error(t("Zaznacz routery")); return; }
    try {
      const { data } = await api.post("/tr069/tasks/bulk", { device_ids: selIds, type, params: params || {} });
      if (!data.queued) { toast.warning(t("Nic nie zlecono — wybrane routery nie mają zapisanego wzorca konfiguracji")); return; }
      toast.success(`${t("Zlecono zadanie dla routerów")}: ${data.queued}`);
      setTimeout(load, 500);
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd zlecenia")); }
  };
  const bulkFw = () => {
    const url = fwUrl.trim();
    if (!url) { toast.error(t("Podaj URL firmware")); return; }
    if (window.confirm(t("Wysłać aktualizację firmware do zaznaczonych routerów?"))) bulk("firmware", { url });
  };
  const saveCfg = async () => {
    try { await api.put("/tr069/config", cfg); toast.success(t("Zapisano ustawienia ACS")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd zapisu")); }
  };
  const openDetail = (d) => {
    setDetail(d); setDFw(""); setPGet(""); setPSet({ name: "", value: "" }); setHist([]);
    const dc = d.desired_config || {};
    setDCfg({ wifi_ssid: dc.wifi_ssid || "", wifi_pass: dc.wifi_pass || "", pppoe_user: dc.pppoe_user || "", pppoe_pass: dc.pppoe_pass || "" });
    api.get(`/tr069/devices/${d.id}/history`).then((r) => setHist(r.data || [])).catch(() => {});
  };
  const refreshDetail = async (id) => {
    try { const { data } = await api.get(`/tr069/devices/${id}`); setDetail(data); load(); } catch { /* noop */ }
    api.get(`/tr069/devices/${id}/history`).then((r) => setHist(r.data || [])).catch(() => {});
  };
  const devTask = async (type, params, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/tr069/devices/${detail.id}/tasks`, { type, params: params || {} });
      toast.success(t("Zlecono — wykona się przy najbliższym kontakcie routera")); setTimeout(() => refreshDetail(detail.id), 800); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd zlecenia")); }
  };
  const saveDevCfg = async () => {
    try { await api.post(`/tr069/devices/${detail.id}/config`, dCfg); toast.success(t("Zapisano wzorzec konfiguracji")); refreshDetail(detail.id); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd")); }
  };
  const provisionNow = async () => {
    try { await api.post(`/tr069/devices/${detail.id}/provision`); toast.success(t("Zlecono provision")); setTimeout(() => refreshDetail(detail.id), 800); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd provision")); }
  };
  const assignClient = async (cid) => {
    try { await api.post(`/tr069/devices/${detail.id}/assign`, { client_id: cid || null }); setDetail({ ...detail, client_id: cid || null }); load(); toast.success(t("Zapisano przypisanie")); }
    catch { toast.error(t("Błąd")); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col h-full" data-testid="routers-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 text-slate-200 font-semibold"><Router size={18} /> {t("Routery klientów (TR-069)")}</div>
        <button onClick={onClose} data-testid="routers-close" className="p-1 rounded hover:bg-slate-700 text-slate-400"><X size={18} /></button>
      </div>

      <div className="px-4 py-3 border-b border-slate-800 bg-blue-950/30 text-[11px] text-slate-300 space-y-1.5" data-testid="acs-instructions">
        <div className="flex items-center gap-1.5 text-blue-300 font-semibold text-xs"><Info size={13} /> {t("Jak podłączyć router")}</div>
        <p>{t("1. W routerze wejdź w TR-069 / CWMP i wpisz adres ACS URL:")}</p>
        <div className="flex items-center gap-1.5">
          <code className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 font-mono text-[11px] text-cyan-300 truncate" data-testid="acs-url">{acsUrl}</code>
          <button onClick={copyUrl} data-testid="acs-url-copy" title={t("Kopiuj")} className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 shrink-0"><ClipboardCopy size={13} /></button>
        </div>
        <p>{t("2. Po zapisaniu router sam zgłosi się i pojawi na liście poniżej (auto-rejestracja).")}</p>
        <p className="text-slate-400">{t("Aby po resecie fabrycznym router odzyskał adres ACS bez dojazdu — ustaw DHCP Option 43 na serwerze DHCP w sieci.")}</p>
      </div>

      {isAdmin && cfg && (
        <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30" data-testid="acs-settings">
          <div className="text-[10px] text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Shield size={12} /> {t("Bezpieczeństwo ACS")}</div>
          <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
            <input type="checkbox" checked={!!cfg.acs_auth_enabled} onChange={(e) => setCfg({ ...cfg, acs_auth_enabled: e.target.checked })} data-testid="acs-auth-enabled" />
            {t("Wymagaj logowania routerów (HTTP Basic)")}
          </label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input value={cfg.acs_user || ""} onChange={(e) => setCfg({ ...cfg, acs_user: e.target.value })} placeholder={t("Login ACS")} data-testid="acs-user"
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
            <input type="password" value={cfg.acs_pass || ""} onChange={(e) => setCfg({ ...cfg, acs_pass: e.target.value })} placeholder={t("Hasło ACS")} data-testid="acs-pass"
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
            <input type="checkbox" checked={cfg.auto_provision !== false} onChange={(e) => setCfg({ ...cfg, auto_provision: e.target.checked })} data-testid="acs-autoprov" />
            {t("Auto-przywracanie konfiguracji po resecie fabrycznym (BOOTSTRAP)")}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
            <span className="text-slate-400">{t("Auto-odświeżanie parametrów co (sek., 0 = wyłączone)")}:</span>
            <input type="number" min="0" step="60" value={cfg.refresh_interval_sec ?? 300} onChange={(e) => setCfg({ ...cfg, refresh_interval_sec: Number(e.target.value) })} data-testid="acs-refresh-interval"
              className="w-24 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" />
          </label>
          <button onClick={saveCfg} data-testid="acs-save" className="bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-1.5 text-xs flex items-center gap-1.5"><Save size={13} /> {t("Zapisz ustawienia ACS")}</button>
        </div>
      )}

      <div className="px-4 py-2.5 border-b border-slate-800 flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {["all", "online", "offline"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`routers-filter-${f}`}
              className={`px-2.5 py-1 rounded text-xs ${filter === f ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
              {f === "all" ? t("Wszystkie") : f === "online" ? t("online") : t("offline")}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Szukaj (model / S-N / firmware / klient)")} data-testid="routers-search"
          className="flex-1 min-w-[160px] bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
        <button onClick={load} data-testid="routers-refresh" className="p-1.5 rounded hover:bg-slate-700 text-slate-400"><RefreshCw size={15} /></button>
      </div>

      {selIds.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-800 bg-slate-800/40 flex flex-wrap items-center gap-2" data-testid="routers-bulkbar">
          <span className="text-xs text-slate-400">{t("Zaznaczono")}: {selIds.length}</span>
          <button onClick={() => bulk("reboot")} data-testid="routers-bulk-reboot" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 text-xs flex items-center gap-1"><Power size={12} /> {t("Restart")}</button>
          <button onClick={() => bulk("provision")} data-testid="routers-bulk-provision" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 text-xs">{t("Provision")}</button>
          <input value={fwUrl} onChange={(e) => setFwUrl(e.target.value)} placeholder={t("URL firmware (.bin)")} data-testid="routers-bulk-fw-url"
            className="flex-1 min-w-[140px] bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
          <button onClick={bulkFw} data-testid="routers-bulk-fw" className="bg-amber-700 hover:bg-amber-600 rounded px-2.5 py-1 text-xs flex items-center gap-1"><Upload size={12} /> {t("Firmware")}</button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm" data-testid="routers-table">
          <thead className="text-[10px] text-cyan-400/80 uppercase tracking-wider sticky top-0 bg-slate-950/80 backdrop-blur-md">
            <tr className="border-b border-slate-800">
              <th className="p-2 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="routers-check-all" /></th>
              <th className="p-2 text-left">{t("Status")}</th>
              <th className="p-2 text-left">{t("Model")}</th>
              <th className="p-2 text-left">S/N</th>
              <th className="p-2 text-left">{t("Klient")}</th>
              <th className="p-2 text-left">Firmware</th>
              <th className="p-2 text-left">IP</th>
              <th className="p-2 text-left">{t("Ost. kontakt")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-slate-500 text-xs">{t("Brak routerów")}</td></tr>
            ) : rows.map((d) => {
              const on = isOnline(d);
              return (
                <tr key={d.id} data-testid={`routers-row-${d.id}`} onClick={() => openDetail(d)} className="border-b border-slate-800/60 hover:bg-cyan-500/5 hover:border-cyan-500/20 transition-colors cursor-pointer">
                  <td className="p-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={!!sel[d.id]} onChange={() => toggle(d.id)} data-testid={`routers-check-${d.id}`} /></td>
                  <td className="p-2"><span className={`inline-flex items-center gap-1 text-[11px] ${on ? "text-green-300" : "text-slate-500"}`}><CircleDot size={11} /> {on ? t("online") : t("offline")}</span></td>
                  <td className="p-2 text-slate-200">{modelOf(d)}</td>
                  <td className="p-2 font-mono text-slate-400 text-xs">{d.serial || "—"}</td>
                  <td className="p-2 text-slate-300 text-xs">{clients[d.client_id] || <span className="text-slate-600">{t("nieprzypisany")}</span>}</td>
                  <td className="p-2 text-slate-400 text-xs">{fwOf(d)}</td>
                  <td className="p-2 font-mono text-slate-400 text-xs">{(d.params || {})[P_IP] || "—"}</td>
                  <td className="p-2 text-slate-500 text-xs">{d.last_inform ? new Date(d.last_inform).toLocaleString("pl-PL") : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="absolute inset-0 bg-slate-900 z-20 flex flex-col" data-testid="router-detail">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <button onClick={() => setDetail(null)} data-testid="router-detail-back" className="flex items-center gap-1.5 text-slate-300 hover:text-white text-sm"><ChevronLeft size={16} /> {t("Wróć do listy")}</button>
            <span className={`inline-flex items-center gap-1 text-[11px] ${isOnline(detail) ? "text-green-300" : "text-slate-500"}`}><CircleDot size={11} /> {isOnline(detail) ? t("online") : t("offline")}</span>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-4">
            <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
              <div className="flex items-center gap-2 text-slate-100 font-semibold"><Cpu size={16} className="text-cyan-400" /> {modelOf(detail)}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mt-2">
                <div className="text-slate-500">S/N</div><div className="text-slate-200 font-mono">{detail.serial || "—"}</div>
                <div className="text-slate-500">Firmware</div><div className="text-slate-200">{fwOf(detail)}</div>
                <div className="text-slate-500">IP</div><div className="text-slate-200 font-mono">{(detail.params || {})[P_IP] || "—"}</div>
                <div className="text-slate-500">{t("Ost. kontakt")}</div><div className="text-slate-200">{detail.last_inform ? new Date(detail.last_inform).toLocaleString("pl-PL") : "—"}</div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <select value={detail.client_id || ""} onChange={(e) => assignClient(e.target.value)} data-testid="router-assign-client" disabled={!isAdmin}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200">
                  <option value="">{t("— nieprzypisany —")}</option>
                  {Object.entries(clients).map(([id, nm]) => <option key={id} value={id}>{nm}</option>)}
                </select>
                {detail.client_id && (
                  <button onClick={() => window.dispatchEvent(new CustomEvent("fp-open-client360", { detail: { clientId: detail.client_id } }))} data-testid="router-open-client360"
                    className="bg-cyan-700 hover:bg-cyan-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1 whitespace-nowrap"><User size={13} /> {t("Karta 360°")}</button>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="rounded-lg border border-slate-800 p-3 space-y-2">
                <div className="text-[10px] text-slate-500 uppercase">{t("Akcje zdalne")}</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => devTask("reboot", {}, t("Zrestartować router?"))} data-testid="router-reboot" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><RotateCcw size={13} /> {t("Restart")}</button>
                  <button onClick={() => devTask("refresh", {})} data-testid="router-refresh-params" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><RefreshCw size={13} /> {t("Odśwież parametry")}</button>
                  <button onClick={() => devTask("factoryreset", {}, t("UWAGA: reset fabryczny wymaże konfigurację routera. Kontynuować?"))} data-testid="router-factory" className="bg-red-800 hover:bg-red-700 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><Power size={13} /> {t("Reset fabryczny")}</button>
                </div>
                <div className="flex gap-2 pt-1">
                  <input value={dFw} onChange={(e) => setDFw(e.target.value)} placeholder={t("URL firmware (.bin)")} data-testid="router-fw-url" className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                  <button onClick={() => dFw.trim() ? devTask("firmware", { url: dFw.trim() }, t("Wysłać aktualizację firmware do routera?")) : toast.error(t("Podaj URL firmware"))} data-testid="router-fw-send" className="bg-amber-700 hover:bg-amber-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><Upload size={13} /> {t("Firmware")}</button>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="rounded-lg border border-slate-800 p-3 space-y-2">
                <div className="text-[10px] text-slate-500 uppercase">{t("Konfiguracja (wzorzec + provision)")}</div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={dCfg.wifi_ssid} onChange={(e) => setDCfg({ ...dCfg, wifi_ssid: e.target.value })} placeholder="WiFi SSID" data-testid="router-cfg-ssid" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                  <input value={dCfg.wifi_pass} onChange={(e) => setDCfg({ ...dCfg, wifi_pass: e.target.value })} placeholder={t("WiFi hasło")} data-testid="router-cfg-wifipass" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                  <input value={dCfg.pppoe_user} onChange={(e) => setDCfg({ ...dCfg, pppoe_user: e.target.value })} placeholder="PPPoE login" data-testid="router-cfg-pppoeuser" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                  <input value={dCfg.pppoe_pass} onChange={(e) => setDCfg({ ...dCfg, pppoe_pass: e.target.value })} placeholder="PPPoE hasło" data-testid="router-cfg-pppoepass" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveDevCfg} data-testid="router-cfg-save" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><Save size={13} /> {t("Zapisz wzorzec")}</button>
                  <button onClick={provisionNow} data-testid="router-provision" className="bg-cyan-700 hover:bg-cyan-600 rounded px-2.5 py-1.5 text-xs">{t("Provision teraz")}</button>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="rounded-lg border border-slate-800 p-3 space-y-2">
                <div className="text-[10px] text-slate-500 uppercase">{t("Parametry TR-069")}</div>
                <div className="flex gap-2">
                  <input value={pGet} onChange={(e) => setPGet(e.target.value)} placeholder="np. InternetGatewayDevice.DeviceInfo." data-testid="router-param-getname" className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 font-mono" />
                  <button onClick={() => pGet.trim() ? devTask("getparams", { names: [pGet.trim()] }) : toast.error(t("Podaj nazwę parametru"))} data-testid="router-param-get" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs">{t("Pobierz")}</button>
                </div>
                <div className="flex gap-2">
                  <input value={pSet.name} onChange={(e) => setPSet({ ...pSet, name: e.target.value })} placeholder={t("Nazwa parametru")} data-testid="router-param-setname" className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 font-mono" />
                  <input value={pSet.value} onChange={(e) => setPSet({ ...pSet, value: e.target.value })} placeholder={t("Wartość")} data-testid="router-param-setval" className="w-32 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200" />
                  <button onClick={() => pSet.name.trim() ? devTask("setparams", { pairs: [{ name: pSet.name.trim(), value: pSet.value }] }) : toast.error(t("Podaj nazwę parametru"))} data-testid="router-param-set" className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><Send size={12} /> {t("Ustaw")}</button>
                </div>
                <div className="max-h-56 overflow-auto rounded border border-slate-800 mt-1" data-testid="router-params-list">
                  <table className="w-full text-[11px]"><tbody>
                    {Object.entries(detail.params || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-800/60"><td className="px-2 py-1 font-mono text-slate-400 break-all">{k}</td><td className="px-2 py-1 text-slate-200 break-all">{String(v)}</td></tr>
                    ))}
                    {Object.keys(detail.params || {}).length === 0 && <tr><td className="px-2 py-3 text-center text-slate-500">{t("Brak parametrów — kliknij „Odśwież parametry”")}</td></tr>}
                  </tbody></table>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-slate-800 p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-2">{t("Historia zdarzeń")}</div>
              <div className="space-y-1 max-h-40 overflow-auto">
                {hist.length === 0 ? <div className="text-[11px] text-slate-500">{t("Brak zdarzeń")}</div> : hist.map((h, i) => (
                  <div key={i} className="text-[11px] flex gap-2"><span className="text-slate-500 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString("pl-PL") : ""}</span><span className="text-slate-300">{[h.kind || h.task_type, h.events, h.status].filter(Boolean).join(" · ")}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
